import com.varabyte.kobweb.browser.api
import com.varabyte.kobweb.core.AppGlobals
import com.varabyte.kobweb.navigation.BasePath
import com.varabyte.kobweb.navigation.Router
import com.varabyte.kobweb.navigation.UpdateHistoryMode
import com.varabyte.kobweb.navigation.remove
import com.varabyte.kobweb.silk.defer.DeferringHost
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.dom.hasClass
import kotlinx.dom.removeClass
import org.jetbrains.compose.web.renderComposable
import org.w3c.dom.EventSource
import org.w3c.dom.EventSourceInit
import org.w3c.dom.MessageEvent
import org.w3c.dom.`get`
import com.routexk.quiz.pages.CheckoutPage as _com_routexk_quiz_pages_CheckoutPage
import com.routexk.quiz.pages.EmailCapturePage as _com_routexk_quiz_pages_EmailCapturePage
import com.routexk.quiz.pages.HomePage as _com_routexk_quiz_pages_HomePage
import com.routexk.quiz.pages.NotFoundPage as _com_routexk_quiz_pages_NotFoundPage
import com.routexk.quiz.pages.PlanPage as _com_routexk_quiz_pages_PlanPage
import com.routexk.quiz.pages.QuizPage as _com_routexk_quiz_pages_QuizPage
import com.routexk.quiz.pages.ReportPage as _com_routexk_quiz_pages_ReportPage
import com.routexk.quiz.pages.ResultsPage as _com_routexk_quiz_pages_ResultsPage
import com.routexk.quiz.pages.SuccessPage as _com_routexk_quiz_pages_SuccessPage

private fun forceReloadNow() {
    window.stop()
    window.location.reload()
}

private fun handleServerStatusEvents() {
    val status = document.getElementById("status")!!
    var lastVersion: Int? = null
    var shouldReload = false

    val warningIcon = status.children[0]!!
    val spinnerIcon = status.children[1]!!
    val statusText = status.children[2]!!

    status.addEventListener("transitionend", {
        if (status.hasClass("fade-out")) {
            status.removeClass("fade-out")
            if (shouldReload) {
                forceReloadNow()
            }
        }
    })

    val eventSource = EventSource("/api/kobweb-status", EventSourceInit(true))
    eventSource.addEventListener("version", { evt ->
        val version = (evt as MessageEvent).data.toString().toInt()
        if (lastVersion == null) {
            lastVersion = version
        }
        if (lastVersion != version) {
            lastVersion = version
            if (document.asDynamic().hidden) {
                // Reload immediately when the page is not visible as the animation will not run
                forceReloadNow()
            } else if (status.className.isNotEmpty()) {
                shouldReload = true
            } else {
                // Not sure if we can get here but if we can't rely on the status transition
                // to reload we should do it ourselves.
                forceReloadNow()
            }
        }
    })

    eventSource.addEventListener("status", { evt ->
        val values: dynamic = JSON.parse<Any>((evt as MessageEvent).data.toString())
        val text = values.text as String
        val isError = (values.isError as String).toBoolean()
        if (text.isNotBlank()) {
            warningIcon.className = if (isError) "visible" else "hidden"
            spinnerIcon.className = if (isError) "hidden" else "visible"
            statusText.innerHTML = "<i>$text</i>"
            status.className = "fade-in"
        } else {
            if (status.className == "fade-in") {
                status.className = "fade-out"
            }
        }
    })

    eventSource.onerror = { eventSource.close() }
}

public fun main() {
    handleServerStatusEvents()

    window.api.logOnError = true

    AppGlobals.initialize(mapOf("title" to "RouteXK"))
    BasePath.set("")
    val router = Router()
    com.varabyte.kobweb.core.init.initKobweb(router) { ctx ->
        ctx.router.register(
            "/",
        ) { pageCtx -> 
            _com_routexk_quiz_pages_HomePage()
        }
        ctx.router.register(
            "/404",
        ) { pageCtx -> 
            _com_routexk_quiz_pages_NotFoundPage()
        }
        ctx.router.register(
            "/checkout",
        ) { pageCtx -> 
            _com_routexk_quiz_pages_CheckoutPage()
        }
        ctx.router.register(
            "/email",
        ) { pageCtx -> 
            _com_routexk_quiz_pages_EmailCapturePage()
        }
        ctx.router.register(
            "/plan",
        ) { pageCtx -> 
            _com_routexk_quiz_pages_PlanPage()
        }
        ctx.router.register(
            "/quiz",
        ) { pageCtx -> 
            _com_routexk_quiz_pages_QuizPage()
        }
        ctx.router.register(
            "/report",
        ) { pageCtx -> 
            _com_routexk_quiz_pages_ReportPage()
        }
        ctx.router.register(
            "/results",
        ) { pageCtx -> 
            _com_routexk_quiz_pages_ResultsPage()
        }
        ctx.router.register(
            "/success",
        ) { pageCtx -> 
            _com_routexk_quiz_pages_SuccessPage()
        }

    }
    router.addRouteInterceptor {
        path = path.removeSuffix(".html").removeSuffix(".htm")
    }

    com.varabyte.kobweb.silk.init.additionalSilkInitialization = { ctx ->
        com.varabyte.kobweb.silk.init.initSilkWidgets(ctx)
        com.varabyte.kobweb.silk.init.initSilkWidgetsKobweb(ctx)
        ctx.theme.registerStyle("service-card", com.routexk.quiz.ui.sections.services.components.ServiceCardStyle)
        ctx.theme.registerStyle("project-card", com.routexk.quiz.ui.sections.projects.components.ProjectCardStyle)
        ctx.theme.registerStyle("footer", com.routexk.quiz.ui.sections.footer.FooterStyle)
        ctx.theme.registerStyle("footer-link", com.routexk.quiz.ui.sections.footer.FooterLinkStyle)
        ctx.theme.registerStyle("social-icon", com.routexk.quiz.ui.sections.footer.SocialIconStyle)
        ctx.theme.registerStyle("nav-header", com.routexk.quiz.ui.sections.header.NavHeaderStyle)
        ctx.theme.registerStyle("nav-link", com.routexk.quiz.ui.sections.header.NavLinkStyle)
        ctx.theme.registerStyle("icon-button", com.routexk.quiz.ui.sections.header.IconButtonStyle)
        ctx.theme.registerStyle("back-to-top-button", com.routexk.quiz.ui.components.widgets.BackToTopButtonStyle)
        ctx.theme.registerStyle("arrow", com.routexk.quiz.ui.components.widgets.ArrowStyle)
        ctx.theme.registerKeyframes("side-menu-slide-in", com.routexk.quiz.ui.sections.header.SideMenuSlideInAnim)
        ctx.theme.registerKeyframes("toast-slide-in", com.routexk.quiz.ui.components.notifications.ToastSlideInKeyframes)
        ctx.theme.registerKeyframes("cookie-slide-up", com.routexk.quiz.ui.components.widgets.CookieSlideUpKeyframes)
        ctx.theme.registerKeyframes("fade-in", com.routexk.quiz.ui.components.widgets.FadeInKeyframes)
        ctx.theme.registerKeyframes("shimmer", com.routexk.quiz.ui.components.widgets.ShimmerKeyframes)
        ctx.theme.registerKeyframes("spin", com.routexk.quiz.ui.components.widgets.SpinKeyframes)
        ctx.theme.registerKeyframes("modal-fade-in", com.routexk.quiz.ui.components.overlay.ModalFadeInKeyframes)
        ctx.theme.registerKeyframes("modal-overlay-fade-in", com.routexk.quiz.ui.components.overlay.ModalOverlayFadeInKeyframes)
        com.routexk.quiz.initSilk(ctx)
    }

    // Dedup any leading slashes after removing the origin, just in case someone typed
    // something like `https://site.com//about` by accident. If we pass `//about` into
    // `tryRoutingTo`, Kobweb will reject it as a protocol-relative URL; instead, we
    // want it to navigate to `/about`
    router.tryRoutingTo(BasePath.remove("/" + window.location.href.removePrefix(window.origin).trimStart('/')), UpdateHistoryMode.REPLACE)

    // For SEO, we may bake the contents of a page in at build time. However, we will
    // overwrite them the first time we render this page with their composable, dynamic
    // versions. Think of this as poor man's hydration :)
    // See also: https://en.wikipedia.org/wiki/Hydration_(web_development)
    val root = document.getElementById("_kobweb-root")!!
    while (root.firstChild != null) { root.removeChild(root.firstChild!!) }

    renderComposable(rootElementId = "_kobweb-root") {
        com.routexk.quiz.AppEntry {
            router.renderActivePage { DeferringHost { it() } }
        }
    }
}
